import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import DeleteIcon from "@mui/icons-material/Delete";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { GetSortString, useTableColumnState } from "components/Table";
import StatusLabel from "components/StatusLabel";
import { ArrowDropDown } from "@mui/icons-material";
import { FilterButton, FilterFreeInput } from "../Activity/filterbar";
import { formatDateTime, fromUnix } from "helpers/dateFormat";


interface ICAPSessionModel {
    users: ICAPSessionItem[]
}

interface ICAPSessionItem {
    id: string
    username: string
    status: string
    ip: string
    lastTimeActive: Date
    expireAt: Date | null
    groups: string[]
}


interface ICAPSessionsProps {

}

type columnsType = "userName" | "status" | "ip" | "groups" | "lastTimeActive" | "expireAt";

export default function ICAPSessions(props: ICAPSessionsProps) {
    const login = useLogin();
    const notification = useNotification();
    const [finishedLoading, setFinishedLoading] = useState(false);
    const [deleteDialogText, setDeleteDialogText] = useState("");
    const [deleteDialogUserId, setDeleteDialogUserId] = useState("");

    const [skipItems, setSkipItems] = useState(0);
    const [takeCount, setTakeCount] = useState(25);
    const [dataCount, setDataCount] = useState(0);

    const [columnsState, setColumnsState] = useTableColumnState<columnsType>([
        { columnId: "userName", width: "25%", sortOrder: 1, direction: "asc" },
        { columnId: "status", width: "10%" },
        { columnId: "ip", width: "15%" },
        { columnId: "groups", width: "20%" },
        { columnId: "lastTimeActive", width: "15%" },
        { columnId: "expireAt", width: "15%" },
    ]);

    const [listState, setListState] = useState<ICAPSessionModel>({ users: [] });

    const [showUsernameFilter, setShowUsernameFilter] = useState(false);
    const [showGroupsFilter, setShowGroupsFilter] = useState(false);

    const [usernamesFilter, setUsernamesFilter] = useState([] as string[]);
    const [groupsFilter, setGroupsFilter] = useState([] as string[]);

    const req = login.GetAxios();

    const loadData = async (c: typeof columnsState, skipItems: number, takeCount: number) => {
        setFinishedLoading(false);
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            let sortBy = GetSortString(c);

            const resp = await req.get("/api/users", {
                params: {
                    sortBy,
                    skip: skipItems,
                    take: takeCount,
                    username: usernamesFilter,
                    groups: groupsFilter,
                }
            });

            setSkipItems(skipItems);
            setTakeCount(takeCount);
            setColumnsState(c);
            setListState({
                users: (resp.data.users || []).map((x: any) => {
                    const item: ICAPSessionItem = {
                        id: x.id,
                        username: x.userName,
                        status: x.status,
                        ip: x.ip,
                        lastTimeActive: fromUnix(x.lastTimeActive),
                        expireAt: x.expireAt ? fromUnix(x.expireAt) : null,
                        groups: x.groups,
                    };
                    return item;
                })
            });
            setDataCount(resp.data.count);

            setFinishedLoading(true);
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return;
            }
            notification.Display({
                title: "Error",
                message: "Could not user fetch data",
                type: "error",
            });
        }
    };

    const deleteUser = async () => {
        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            const resp = await req.post("/api/users/delete", {
                UserIds: [deleteDialogUserId],
            });

            const result = resp.data.Results[0];

            if (!result.Success) {
                notification.Display({
                    title: "Error",
                    message: "Could not delete user",
                    type: "error",
                });
                console.error(result.Error);
                return;
            }

            setDeleteDialogUserId("");
            loadData(columnsState, skipItems, takeCount);
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: "Could not delete user",
                type: "error",
            });
        }
    };

    const deleteUserDialog = (user: ICAPSessionItem) => {
        setDeleteDialogUserId(user.id);
        setDeleteDialogText(`Are you sure you want to expire all sessions for ${user.username}? The user stays in the list; a new session is created on their next request.`);
    };

    useEffect(() => {
        loadData(columnsState, skipItems, takeCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadData(columnsState, 0, 25);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usernamesFilter, groupsFilter]);

    return <>
        <Box display="flex">
            <Typography variant="h1">Policy Users</Typography>
        </Box>

        <Stack direction={"row"} gap={1} marginBottom={"10px"} marginTop={"10px"}>
            <Stack direction={"row"} gap={2}>
                <FilterButton
                    name={"Username"}
                    open={showUsernameFilter}
                    onClick={() => {
                        setShowUsernameFilter(true);
                    }}
                    onMenuClose={() => setShowUsernameFilter(false)}
                    active={usernamesFilter.length > 0}
                    endIcon={<ArrowDropDown fontSize="small" />}
                >
                    <FilterFreeInput
                        items={usernamesFilter}
                        inputPlaceholder="Search Username"
                        itemAdded={(x) => {
                            setUsernamesFilter(usernamesFilter.concat(x));
                            return undefined;
                        }}
                        clearItems={() => {
                            setUsernamesFilter([]);
                        }}
                        itemDeleted={(x) => {
                            setUsernamesFilter(usernamesFilter.filter(y => y !== x));
                        }}
                    />
                </FilterButton>
                <FilterButton
                    name={"Groups"}
                    open={showGroupsFilter}
                    onClick={() => {
                        setShowGroupsFilter(true);
                    }}
                    onMenuClose={() => setShowGroupsFilter(false)}
                    active={groupsFilter.length > 0}
                    endIcon={<ArrowDropDown fontSize="small" />}
                >
                    <FilterFreeInput
                        items={groupsFilter}
                        inputPlaceholder="Search Groups"
                        itemAdded={(x) => {
                            setGroupsFilter(groupsFilter.concat(x));
                            return undefined;
                        }}
                        clearItems={() => {
                            setGroupsFilter([]);
                        }}
                        itemDeleted={(x) => {
                            setGroupsFilter(groupsFilter.filter(y => y !== x));
                        }}
                    />
                </FilterButton>
            </Stack>
            <Stack flexGrow={1} direction={"row"} justifyContent={"end"}>
                <Button variant="outlined" onClick={() => {
                    setUsernamesFilter([]);
                    setGroupsFilter([]);
                }}>Clear Filters</Button>
            </Stack>
        </Stack>
        <DataTable
            sx={{
                maxHeight: "90%",
            }}
            size="medium"
            columnsDataId={"icapUsersTablev2"}
            data={listState.users}
            dataCount={dataCount}
            finishedLoading={finishedLoading}
            paginationStartItem={skipItems}
            paginationItemCount={takeCount}
            onPaginationStartItemChanged={(x) => {
                loadData(columnsState, x, takeCount);
            }}
            onPaginationItemCountChanged={(x) => {
                loadData(columnsState, skipItems, x);
            }}
            onSortChanged={(x) => loadData(x, skipItems, takeCount)}
            onColumnOrderChanged={(x) => setColumnsState(x)}
            columnsState={columnsState}
            columns={{
                userName: {
                    displayName: "Username",
                    sortable: true,
                    render: (x) => {
                        return x.username;
                    },
                },
                status: {
                    displayName: "Status",
                    sortable: true,
                    render: (x) => {
                        const active = x.status === "active";
                        return <StatusLabel label={active ? "Active" : "Inactive"} color={active ? "success" : "disabled"} />;
                    },
                },
                ip: {
                    displayName: "IP Address",
                    sortable: false,
                    render: (x) => {
                        return x.ip || "-";
                    },
                },
                groups: {
                    displayName: "Groups",
                    sortable: false,
                    render: (x) => {
                        return x.groups.join(", ");
                    },
                },
                lastTimeActive: {
                    displayName: "Last Time Active",
                    sortable: true,
                    render: (x) => {
                        return formatDateTime(x.lastTimeActive);
                    },
                },
                expireAt: {
                    displayName: "Expire At",
                    sortable: false,
                    render: (x) => {
                        return x.expireAt ? formatDateTime(x.expireAt) : "-";
                    },
                }
            }}
            rowOptionsMenu={[
                {
                    icon: <DeleteIcon className="delete-button" fontSize="small" />,
                    text: "Expire Sessions",
                    onClick: (x) => {
                        deleteUserDialog(x);
                    }
                },
            ]}
        />

        <ConfirmDialog
            open={deleteDialogUserId !== ""}
            title="Expire Sessions"
            message={deleteDialogText}
            confirmLabel="Expire"
            onConfirm={deleteUser}
            onCancel={() => setDeleteDialogUserId("")}
        />
    </>;
}